import axios from 'axios';
import logger, { Logger } from '../utils/logger';

const API_URL = 'https://mapi2.3sigmacrm.com/api/v1';

// Types matching actual API response
export interface Campaign {
  _id: string;
  name: string;
  serviceType: string;
  createdBy: string;
  organization: string;
  whatsappTemplate: string;
  content: null;
  status: 'pending' | 'complete' | 'cancelled' | 'approved' | 'rejected';
  senderDetail: {
    email: string | null;
    name: string | null;
  };
  scheduleAt: string | null;
  totalQueued: number;
  totalProcessed: number;
  isCompleted: boolean;
}

// Campaign creation payload type
export interface CampaignPayload {
  name: string;
  serviceType: string;
  scheduleAt: string | Date;
  whatsappTemplate?: string;
  content?: string;
  leads: string[];
  senderDetail?: {
    email: string;
    name: string;
  };
  dailyLimit?: number;
  integrationId?: string;
}

export interface CampaignLead {
  id: string;
  name: string;
  assignedTo: string;
  createdAt: string;
  lastActivity: string;
  actions: {
    canCall: boolean;
    canEmail: boolean;
    canWhatsapp: boolean;
  };
}

interface ApiResponse<T> {
  statusCode: number;
  status: boolean;
  data: T;
  messagingLimitTier?: string;
  dailyMailTracker?: Array<{
    type: string;
    isTodayLimitReached: boolean;
    allowedLimit: number;
    todayRemainingLimit: number;
  }>;
}

interface CampaignDetailResponse extends ApiResponse<Campaign> {
  leads?: CampaignLead[];
  total?: number;
}

// Get auth headers
const getHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: localStorage.getItem('auth_token') || '',
});

export const campaignService = {
  // Get campaign details including leads with search and status filters
  getCampaignDetails: async (
    campaignId: string, 
    params?: { 
      search?: string;
      status?: string;
      page?: number;
      perPage?: number;
    }
  ) => {
    logger.log('Fetching campaign details:', { campaignId, params });
    Logger.log('Fetching campaign details:', { campaignId, params });

    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.perPage) queryParams.append('perPage', params.perPage.toString());

    const url = `${API_URL}/campaign/${campaignId}${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    logger.log('API URL:', url);
    Logger.log('API URL:', url);

    try {
      const response = await axios.get<CampaignDetailResponse>(url, {
        headers: getHeaders()
      });
      
      logger.log('API Response:', response.data);
      Logger.log('API Response:', response.data);
      return response;

    } catch (error) {
      console.error('Error fetching campaign details:', error);
      throw error;
    }
  },

  // Search leads within a campaign
  searchCampaignLeads: async (
    campaignId: string,
    search: string,
    page: number = 1,
    perPage: number = 20
  ) => {
    logger.log('Searching campaign leads:', { campaignId, search, page, perPage });
    Logger.log('Searching campaign leads:', { campaignId, search, page, perPage });

    const queryParams = new URLSearchParams({
      search,
      page: page.toString(),
      perPage: perPage.toString()
    });

    const url = `${API_URL}/campaign/${campaignId}/leads?${queryParams.toString()}`;
    
    try {
      const response = await axios.get<ApiResponse<CampaignLead[]>>(url, {
        headers: getHeaders()
      });
      
      logger.log('Search Results:', response.data);
      Logger.log('Search Results:', response.data);
      return response;

    } catch (error) {
      console.error('Error searching leads:', error);
      throw error;
    }
  },

  // Get campaign by status
  getCampaignsByStatus: async (status: string) => {
    logger.log('Fetching campaigns by status:', status);
    Logger.log('Fetching campaigns by status:', status);
    
    try {
      const response = await axios.get<ApiResponse<Campaign[]>>(
        `${API_URL}/campaign?status=${status}`,
        { headers: getHeaders() }
      );
      
      logger.log('Status filtered campaigns:', response.data);
      Logger.log('Status filtered campaigns:', response.data);
      return response;

    } catch (error) {
      console.error('Error fetching campaigns by status:', error);
      throw error;
    }
  },
  
  // Create campaign with ZeptoMail support
  createCampaign: async (campaignData: CampaignPayload) => {
    logger.log('Creating campaign with data:', campaignData);
    Logger.log('Creating campaign with data:', campaignData);
    
    try {
      const response = await axios.post(
        `${API_URL}/campaign`,
        campaignData,
        { headers: getHeaders() }
      );
      
      logger.log('Campaign created:', response.data);
      Logger.log('Campaign created:', response.data);
      return response;
    } catch (error) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  }
};